/**
 * RESPONSIBILITY: show an @Img value, or a placeholder shaped like the one that is missing.
 *
 * Every image slot on this site is empty until somebody uploads a photograph, and an
 * empty slot that collapses to nothing makes a half-filled page look broken rather than
 * unfinished. The placeholder is an inline SVG with `preserveAspectRatio="none"`, so it
 * stretches to whatever box the layout gives it and the page keeps its real proportions.
 *
 * One flat colour, taken from a CSS custom property, so it reads as "nothing here yet"
 * and never as a design.
 */
import { hasImage, type ImageValue } from '@framework/core';

export interface PictureProps {
  value: ImageValue | undefined;
  /** Falls back to the alt text stored with the image. */
  alt?: string;
  className?: string;
}

export function Picture({ value, alt, className = '' }: PictureProps) {
  if (hasImage(value)) {
    return <img className={`pic ${className}`} src={value.path} alt={alt ?? value.alt ?? ''} />;
  }

  return (
    <svg
      className={`pic pic-empty ${className}`}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      role="img"
      aria-label={alt || 'Image to be supplied'}
      focusable="false"
    >
      <rect width="100" height="100" fill="currentColor" />
      {/* A mark rather than a bare block, so an empty slot is visibly deliberate.
          Non-scaling strokes keep it from smearing when the box is stretched. */}
      <path
        d="M22 68 L42 44 L56 60 L66 50 L80 68 Z"
        fill="none" stroke="currentColor" strokeWidth="2"
        vectorEffect="non-scaling-stroke" opacity=".55"
      />
      <circle cx="35" cy="34" r="5" fill="none" stroke="currentColor" strokeWidth="2"
              vectorEffect="non-scaling-stroke" opacity=".55" />
    </svg>
  );
}
